import type {ReactElement} from 'react';
import React from 'react';
import {TranscriptFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-font-option/transcript-font-option';
import {TranscriptFontSizeOption} from '@Components/poster-editor/components/transcript-font-size-option/transcript-font-size-option';
import {TranscriptBackgroundOption} from '@Components/poster-editor/components/transcript-background-option/transcript-background-option';
import {TranscriptSentenceTextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-sentence-text-color-option/transcript-sentence-text-color-option';
import {TranscriptActiveWordTextColorOption} from '@Components/poster-editor/components/transcript-active-word-text-color-option/transcript-active-word-text-color-option';
import {TranscriptSentenceOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-sentence-outline-option/transcript-sentence-outline-option';
import {TranscriptActiveWordOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-active-word-outline-option/transcript-active-word-outline-option';
import {TranscriptLetterSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-letter-spacing-option/transcript-letter-spacing-option';
import {TranscriptShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-shadow-option/transcript-shadow-option';
import {TranscriptContextualMenuSubPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-customize-styles-panel/transcript-customize-styles-panel.types';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {useGetSelectedTranscriptItem} from '@Hooks/poster-editor/useGetSelectedTranscriptItem';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {TranscriptLetterCaseOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-letter-case-option/transcript-letter-case-option';
import {TranscriptVerticalAlignOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/transcript-vertical-align-option/transcript-vertical-align-option';
import type {TranscriptCustomizeOptionsContentProps} from './transcript-customize-options-content.types';
import {useGetKeyFromSubtitlePropertiesForTranscriptItem} from '@Hooks/poster-editor/useGetKeyFromSubtitlePropertiesForTranscriptItem';
import {LanguageNotSupportedByTranscriptFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/language-not-supported-by-transcript-font-option/language-not-supported-by-transcript-font-option';

export function TranscriptCustomizeOptionsContent({...props}: TranscriptCustomizeOptionsContentProps): ReactElement<any> {
  const activeItem = useGetSelectedTranscriptItem();
  if (activeItem === null) {
    throw new Error('Transcript Item is undefined in TranscriptCustomizeOptionsContent');
  }

  const animationStyle = useGetKeyFromSubtitlePropertiesForTranscriptItem('animationStyle');
  if (!animationStyle) {
    throw new Error('Transcript item does not have animationStyle property in its subtitle objects');
  }

  const onBackgroundColorChangeOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.TRANSCRIPT_BACKGROUND_COLOR);
  };

  const onSentenceTextColorChangeOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.SENTENCE_TEXT_COLOR);
  };

  const onActiveWordTextColorChangeOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.ACTIVE_WORD_TEXT_COLOR);
  };

  const onSentenceOutlineColorChangeOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.SENTENCE_TEXT_OUTLINE_COLOR);
  };

  const onActiveWordOutlineColorChangeOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.ACTIVE_WORD_TEXT_OUTLINE_COLOR);
  };

  const onFontOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.FONT);
  };

  const onShadowColorChangeOptionTriggered = (): void => {
    props.setSubPanel(TranscriptContextualMenuSubPanel.TRANSCRIPT_SHADOW_COLOR);
  };

  const getText = (): string => {
    switch (activeItem.getAnySubtitle().animationStyle) {
      case SubtitleTemplateType.HIGHLIGHT_WORD:
        return window.i18next.t('pmwjs_highlight_word');
      case SubtitleTemplateType.PROGRESS:
        return window.i18next.t('pmwjs_highlight_words');
      default:
        throw new Error(`Unhandled SubtitleTemplateType for active word: ${activeItem.getAnySubtitle().animationStyle}`);
    }
  };

  const getActiveWordOptions = (): ReactElement<any> => {
    return (
      <>
        <ContextualMenuHeadingOption id="highlight-word-styles-option" text={getText()} />
        <TranscriptActiveWordTextColorOption onActiveWordTextColorFillClick={onActiveWordTextColorChangeOptionTriggered} />
        <TranscriptActiveWordOutlineOption onActiveWordStrokeColorFillClick={onActiveWordOutlineColorChangeOptionTriggered} />
      </>
    );
  };

  return (
    <>
      <TranscriptFontOption onClickFontButton={onFontOptionTriggered} />
      <TranscriptFontSizeOption />
      <LanguageNotSupportedByTranscriptFontOption />
      <TranscriptLetterCaseOption />
      <TranscriptVerticalAlignOption />
      <TranscriptBackgroundOption onTranscriptBackgroundColorFillClicked={onBackgroundColorChangeOptionTriggered} />
      <TranscriptSentenceTextColorOption onSentenceTextColorFillClick={onSentenceTextColorChangeOptionTriggered} />
      <TranscriptSentenceOutlineOption onSentenceStrokeColorFillClick={onSentenceOutlineColorChangeOptionTriggered} />
      <TranscriptLetterSpacingOption />
      <TranscriptShadowOption onTranscriptShadowColorFillClick={onShadowColorChangeOptionTriggered} />
      {animationStyle === SubtitleTemplateType.PROGRESS || animationStyle === SubtitleTemplateType.HIGHLIGHT_WORD ? getActiveWordOptions() : undefined}
    </>
  );
}
